import { useEffect, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FilterContext } from '@components/FilterBar/FilterProvider';
import {
  selectEffectiveGoLiveSourceConfig,
  selectCmsCurrentProductId,
} from '../store/slices/cmsSlice';
import { selectFilterValue } from '../store/slices/filterSliceV2';
import {
  resolveGoLiveSource,
  getGoLiveDateFromSource,
  parseGoLiveDate,
} from '../services/cms/utils/goLiveSourceConfig';

/**
 * Hook to manage goLiveDate resolution using CMS config
 *
 * This hook ONLY handles products that have goLiveSource configured in DatoCMS.
 * Products without CMS config fall back to legacy hardcoded useEffects in useFilterLogic.js.
 *
 * IMPORTANT: To prevent race conditions when switching products, this hook verifies
 * that the CMS config matches the currently selected product in FilterContext.
 *
 * Flow:
 * 1. Reads goLiveSource config from CMS (via Redux)
 * 2. Verifies CMS currentProductId matches FilterContext selectedProduct
 * 3. If CMS config exists AND matches current product, resolves goLiveDate
 * 4. If no CMS config or product mismatch, does nothing (legacy useEffects handle it)
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.setGoLiveMonth - Setter for goLiveMonth state
 * @param {Function} options.setGoLiveYear - Setter for goLiveYear state
 * @returns {Object} goLiveDate resolution state and utilities
 */
export const useGoLiveSource = ({ setGoLiveMonth, setGoLiveYear }) => {
  const filterContext = useContext(FilterContext);

  // Get CMS goLiveSource config from Redux
  const rawCmsGoLiveSourceConfig = useSelector(
    selectEffectiveGoLiveSourceConfig
  );

  // Get the currentProductId from CMS Redux state
  const cmsCurrentProductId = useSelector(selectCmsCurrentProductId);

  // Get current model ID from Redux
  const selectedModelId = useSelector((state) =>
    selectFilterValue(state, 'model')
  );

  // CRITICAL: Only use CMS config if it matches the current FilterContext product
  // This prevents race conditions when switching between products
  const cmsGoLiveSourceConfig = useMemo(() => {
    const filterProductId = filterContext.selectedProduct?.productId;

    // If CMS currentProductId doesn't match FilterContext productId,
    // the CMS data is stale (from previous product) - return null
    if (!filterProductId || cmsCurrentProductId !== filterProductId) {
      return null;
    }

    return rawCmsGoLiveSourceConfig;
  }, [
    rawCmsGoLiveSourceConfig,
    cmsCurrentProductId,
    filterContext.selectedProduct?.productId,
  ]);

  /**
   * Resolves the goLiveSource level using CMS config
   * Returns null if no CMS config is available
   */
  const resolveSource = useCallback(() => {
    // Only resolve if CMS config exists
    if (!cmsGoLiveSourceConfig) {
      return null;
    }

    const modelId = filterContext.selectedModel?.id || selectedModelId;
    return resolveGoLiveSource(cmsGoLiveSourceConfig, modelId);
  }, [filterContext.selectedModel?.id, selectedModelId, cmsGoLiveSourceConfig]);

  /**
   * Gets the goLiveDate value from the appropriate filter selection
   */
  const getGoLiveDateValue = useCallback(
    (source) => {
      if (!source) {
        return null;
      }

      const selections = {
        selectedProduct: filterContext.selectedProduct,
        selectedModel: filterContext.selectedModel,
        selectedSector: filterContext.selectedSector,
        selectedMarket: filterContext.selectedMarket,
        selectedPlant: filterContext.selectedPlant,
      };

      return getGoLiveDateFromSource(source, selections);
    },
    [
      filterContext.selectedProduct,
      filterContext.selectedModel,
      filterContext.selectedSector,
      filterContext.selectedMarket,
      filterContext.selectedPlant,
    ]
  );

  /**
   * Updates the goLiveDate in FilterContext and local state
   */
  const updateGoLiveDate = useCallback(
    (dateString) => {
      const date = parseGoLiveDate(dateString);

      filterContext.setGoLiveDate(date);
      setGoLiveMonth(date.getMonth());
      setGoLiveYear(date.getFullYear());
    },
    [filterContext.setGoLiveDate, setGoLiveMonth, setGoLiveYear]
  );

  // Effect to resolve and update goLiveDate when CMS config exists
  // Only runs when cmsGoLiveSourceConfig is available
  useEffect(() => {
    // Skip if no CMS config - legacy useEffects in useFilterLogic will handle it
    if (!cmsGoLiveSourceConfig) {
      return;
    }

    const productId = filterContext.selectedProduct?.productId;
    if (!productId) {
      return;
    }

    const source = resolveSource();
    if (!source) {
      return;
    }

    const dateValue = getGoLiveDateValue(source);
    updateGoLiveDate(dateValue);
  }, [
    cmsGoLiveSourceConfig,
    filterContext.selectedProduct?.productId,
    filterContext.selectedModel?.id,
    filterContext.selectedSector?.sectorId,
    filterContext.selectedMarket?.marketId,
    filterContext.selectedPlant?.plantId,
    resolveSource,
    getGoLiveDateValue,
    updateGoLiveDate,
  ]);

  return {
    resolveSource,
    getGoLiveDateValue,
    updateGoLiveDate,
    cmsGoLiveSourceConfig,
    isUsingCmsConfig: !!cmsGoLiveSourceConfig,
  };
};

export default useGoLiveSource;
